import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import workflowConfigService from '../services/workflowConfigService';
import {
  Settings, Plus, Edit, Trash2, Copy, Save, X, AlertCircle, CheckCircle,
  Clock, Users, ArrowRight, GripVertical, ChevronUp, ChevronDown, Play,
  Package, Zap, Gift, RotateCcw, AlertOctagon, Eye, Code, FileText
} from 'lucide-react';
import { SmartDropdown } from '../components/ui';
import api from '../services/api';

const WORKFLOW_TYPE_ICONS = {
  STANDARD: Package,
  JIT: Zap,
  FOC: Gift,
  RETURN: RotateCcw,
  CRITICAL: AlertOctagon,
  CUSTOM: Settings
};

const WORKFLOW_TYPE_COLORS = {
  STANDARD: 'blue',
  JIT: 'orange',
  FOC: 'green',
  RETURN: 'yellow',
  CRITICAL: 'red',
  CUSTOM: 'purple'
};

const STEP_TYPES = [
  { value: 'TASK', label: 'Manual Task', description: 'Requires human action', icon: Users },
  { value: 'AUTO', label: 'Automated', description: 'System executes automatically', icon: Play },
  { value: 'DECISION', label: 'Decision Point', description: 'Branching logic', icon: AlertCircle },
  { value: 'PARALLEL', label: 'Parallel', description: 'Multiple steps simultaneously', icon: Copy },
  { value: 'NOTIFICATION', label: 'Notification', description: 'Send alerts', icon: AlertCircle }
];

const WorkflowConfiguration = () => {
  const [activeView, setActiveView] = useState('templates');
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [editingStep, setEditingStep] = useState(null);
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [showStepModal, setShowStepModal] = useState(false);
  const [workflowForm, setWorkflowForm] = useState({
    name: '',
    workflow_type: 'CUSTOM',
    description: '',
    default_sla_hours: 3.0,
    trigger_conditions: {},
    is_active: true
  });
  const [stepForm, setStepForm] = useState({
    name: '',
    step_type: 'TASK',
    description: '',
    order: 1,
    sla_hours: 1.0,
    assigned_role: '',
    skip_conditions: {},
    config: {}
  });
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const queryClient = useQueryClient();

  // Fetch workflow definitions
  const { data: workflows = [], isLoading } = useQuery({
    queryKey: ['workflow-definitions'],
    queryFn: () => workflowConfigService.getWorkflowDefinitions()
  });

  // Fetch selected workflow details
  const { data: workflowDetails } = useQuery({
    queryKey: ['workflow-details', selectedWorkflow?.id],
    queryFn: () => workflowConfigService.getWorkflowDefinition(selectedWorkflow.id),
    enabled: !!selectedWorkflow?.id
  });

  // Fetch roles for dropdown
  const { data: roles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const response = await api.get('/access-control/roles/');
      return response.data?.results || response.data || [];
    }
  });

  // Mutations
  const createWorkflowMutation = useMutation({
    mutationFn: (data) => workflowConfigService.createWorkflowDefinition(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['workflow-definitions']);
      setSuccess('Workflow created successfully!');
      setShowWorkflowModal(false);
      resetWorkflowForm();
    },
    onError: (err) => setError(err.response?.data?.message || 'Failed to create workflow')
  });

  const updateWorkflowMutation = useMutation({
    mutationFn: ({ id, data }) => workflowConfigService.updateWorkflowDefinition(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['workflow-definitions']);
      queryClient.invalidateQueries(['workflow-details']);
      setSuccess('Workflow updated successfully!');
      setShowWorkflowModal(false);
    },
    onError: (err) => setError(err.response?.data?.message || 'Failed to update workflow')
  });

  const deleteWorkflowMutation = useMutation({
    mutationFn: (id) => workflowConfigService.deleteWorkflowDefinition(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['workflow-definitions']);
      setSuccess('Workflow deleted successfully!');
      setSelectedWorkflow(null);
    },
    onError: (err) => setError(err.response?.data?.message || 'Failed to delete workflow')
  });

  const createStepMutation = useMutation({
    mutationFn: (data) => workflowConfigService.createWorkflowStep(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['workflow-details']);
      setSuccess('Step added successfully!');
      setShowStepModal(false);
      resetStepForm();
    },
    onError: (err) => setError(err.response?.data?.message || 'Failed to add step')
  });

  const updateStepMutation = useMutation({
    mutationFn: ({ id, data }) => workflowConfigService.updateWorkflowStep(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['workflow-details']);
      setSuccess('Step updated successfully!');
      setShowStepModal(false);
      setEditingStep(null);
    },
    onError: (err) => setError(err.response?.data?.message || 'Failed to update step')
  });

  const deleteStepMutation = useMutation({
    mutationFn: (id) => workflowConfigService.deleteWorkflowStep(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['workflow-details']);
      setSuccess('Step deleted successfully!');
    },
    onError: (err) => setError(err.response?.data?.message || 'Failed to delete step')
  });

  const cloneWorkflowMutation = useMutation({
    mutationFn: ({ id, newName }) => workflowConfigService.cloneWorkflow(id, newName),
    onSuccess: () => {
      queryClient.invalidateQueries(['workflow-definitions']);
      setSuccess('Workflow cloned successfully!');
    },
    onError: (err) => setError(err.response?.data?.message || 'Failed to clone workflow')
  });

  const resetWorkflowForm = () => {
    setWorkflowForm({
      name: '',
      workflow_type: 'CUSTOM',
      description: '',
      default_sla_hours: 3.0,
      trigger_conditions: {},
      is_active: true
    });
  };

  const resetStepForm = () => {
    setStepForm({
      name: '',
      step_type: 'TASK',
      description: '',
      order: 1,
      sla_hours: 1.0,
      assigned_role: '',
      skip_conditions: {},
      config: {}
    });
  };

  const handleCreateWorkflow = (e) => {
    e.preventDefault();
    createWorkflowMutation.mutate(workflowForm);
  };

  const handleEditWorkflow = (workflow) => {
    setWorkflowForm({
      name: workflow.name,
      workflow_type: workflow.workflow_type,
      description: workflow.description || '',
      default_sla_hours: workflow.default_sla_hours,
      trigger_conditions: workflow.trigger_conditions || {},
      is_active: workflow.is_active
    });
    setShowWorkflowModal(true);
  };

  const handleCreateStep = (e) => {
    e.preventDefault();
    const data = {
      ...stepForm,
      workflow_definition: selectedWorkflow.id,
      order: workflowDetails?.steps?.length ? workflowDetails.steps.length + 1 : 1
    };
    createStepMutation.mutate(data);
  };

  const handleEditStep = (step) => {
    setEditingStep(step);
    setStepForm({
      name: step.name,
      step_type: step.step_type,
      description: step.description || '',
      order: step.order,
      sla_hours: step.sla_hours,
      assigned_role: step.assigned_role || '',
      skip_conditions: step.skip_conditions || {},
      config: step.config || {}
    });
    setShowStepModal(true);
  };

  const handleCloneWorkflow = (workflow) => {
    const newName = prompt(`Enter name for cloned workflow:`, `${workflow.name} (Copy)`);
    if (newName) {
      cloneWorkflowMutation.mutate({ id: workflow.id, newName });
    }
  };

  const handleDeleteWorkflow = (workflow) => {
    if (confirm(`Are you sure you want to delete "${workflow.name}"? This cannot be undone.`)) {
      deleteWorkflowMutation.mutate(workflow.id);
    }
  };

  const handleDeleteStep = (step) => {
    if (confirm(`Are you sure you want to delete step "${step.name}"?`)) {
      deleteStepMutation.mutate(step.id);
    }
  };

  const getWorkflowTypeIcon = (type) => {
    const Icon = WORKFLOW_TYPE_ICONS[type] || Settings;
    return Icon;
  };

  const getWorkflowTypeColor = (type) => {
    return WORKFLOW_TYPE_COLORS[type] || 'gray';
  };

  const formatHours = (hours) => {
    if (hours < 1) {
      return `${Math.round(hours * 60)} min`;
    }
    return `${hours} hrs`;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Settings className="w-8 h-8 text-blue-600" />
              Workflow Configuration
            </h1>
            <p className="text-gray-600 mt-1">Design and manage workflow templates</p>
          </div>
          <button
            onClick={() => {
              resetWorkflowForm();
              setShowWorkflowModal(true);
            }}
            className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors shadow-lg"
          >
            <Plus className="w-5 h-5" />
            New Workflow Template
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            <span>{success}</span>
          </div>
          <button onClick={() => setSuccess('')} className="text-green-600 hover:text-green-800">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Workflow Templates List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Workflow Templates</h2>
              <p className="text-sm text-gray-600 mt-1">{workflows.length} templates</p>
            </div>
            <div className="p-4 space-y-2 max-h-[calc(100vh-300px)] overflow-y-auto">
              {isLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-600 mt-2">Loading...</p>
                </div>
              ) : workflows.length === 0 ? (
                <div className="text-center py-8">
                  <Settings className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-600">No workflows yet</p>
                  <p className="text-sm text-gray-500">Create your first template</p>
                </div>
              ) : (
                workflows.map((workflow) => {
                  const Icon = getWorkflowTypeIcon(workflow.workflow_type);
                  const color = getWorkflowTypeColor(workflow.workflow_type);
                  const isSelected = selectedWorkflow?.id === workflow.id;

                  return (
                    <div
                      key={workflow.id}
                      onClick={() => setSelectedWorkflow(workflow)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        isSelected
                          ? `border-${color}-500 bg-${color}-50`
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg bg-${color}-100`}>
                          <Icon className={`w-5 h-5 text-${color}-600`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 truncate">{workflow.name}</h3>
                          <p className="text-sm text-gray-600 mt-1">{workflow.workflow_type}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatHours(workflow.default_sla_hours)}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              workflow.is_active
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {workflow.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Workflow Details & Step Designer */}
        <div className="lg:col-span-2">
          {selectedWorkflow ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              {/* Workflow Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    {(() => {
                      const Icon = getWorkflowTypeIcon(selectedWorkflow.workflow_type);
                      const color = getWorkflowTypeColor(selectedWorkflow.workflow_type);
                      return (
                        <div className={`p-3 rounded-lg bg-${color}-100`}>
                          <Icon className={`w-8 h-8 text-${color}-600`} />
                        </div>
                      );
                    })()}
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{selectedWorkflow.name}</h2>
                      <p className="text-gray-600 mt-1">{selectedWorkflow.description || 'No description'}</p>
                      <div className="flex items-center gap-3 mt-3">
                        <span className="text-sm text-gray-600">
                          <span className="font-medium">Type:</span> {selectedWorkflow.workflow_type}
                        </span>
                        <span className="text-sm text-gray-600">
                          <span className="font-medium">SLA:</span> {formatHours(selectedWorkflow.default_sla_hours)}
                        </span>
                        <span className="text-sm text-gray-600">
                          <span className="font-medium">Steps:</span> {workflowDetails?.steps?.length || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditWorkflow(selectedWorkflow)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                      title="Edit Workflow"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleCloneWorkflow(selectedWorkflow)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                      title="Clone Workflow"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteWorkflow(selectedWorkflow)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      title="Delete Workflow"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Workflow Steps */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Workflow Steps</h3>
                  <button
                    onClick={() => {
                      resetStepForm();
                      setEditingStep(null);
                      setShowStepModal(true);
                    }}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4" />
                    Add Step
                  </button>
                </div>

                {workflowDetails?.steps?.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-600">No steps defined</p>
                    <p className="text-sm text-gray-500 mt-1">Add your first step to this workflow</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {workflowDetails?.steps?.map((step, index) => {
                      const stepTypeInfo = STEP_TYPES.find(t => t.value === step.step_type);
                      const StepIcon = stepTypeInfo?.icon || Users;

                      return (
                        <div
                          key={step.id}
                          className="border-2 border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-all"
                        >
                          <div className="flex items-start gap-4">
                            {/* Step Order */}
                            <div className="flex-shrink-0">
                              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-600">
                                {step.order}
                              </div>
                            </div>

                            {/* Step Info */}
                            <div className="flex-1">
                              <div className="flex items-start justify-between">
                                <div>
                                  <h4 className="font-semibold text-gray-900 text-lg">{step.name}</h4>
                                  <p className="text-sm text-gray-600 mt-1">{step.description || 'No description'}</p>
                                  <div className="flex flex-wrap gap-2 mt-3">
                                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium flex items-center gap-1">
                                      <StepIcon className="w-3 h-3" />
                                      {stepTypeInfo?.label || step.step_type}
                                    </span>
                                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      SLA: {formatHours(step.sla_hours)}
                                    </span>
                                    {step.assigned_role && (
                                      <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1">
                                        <Users className="w-3 h-3" />
                                        {step.assigned_role}
                                      </span>
                                    )}
                                    {step.skip_conditions && Object.keys(step.skip_conditions).length > 0 && (
                                      <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
                                        Conditional
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Step Actions */}
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleEditStep(step)}
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                                    title="Edit Step"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteStep(step)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                    title="Delete Step"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Arrow to next step */}
                          {index < (workflowDetails?.steps?.length || 0) - 1 && (
                            <div className="flex justify-center my-2">
                              <ArrowRight className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <Eye className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Workflow Selected</h3>
              <p className="text-gray-600">Select a workflow template from the list to view and edit its steps</p>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Workflow Modal */}
      {showWorkflowModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">
                  {workflowForm.name ? 'Edit' : 'Create'} Workflow Template
                </h2>
                <button
                  onClick={() => {
                    setShowWorkflowModal(false);
                    resetWorkflowForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleCreateWorkflow} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Workflow Name *
                </label>
                <input
                  type="text"
                  required
                  value={workflowForm.name}
                  onChange={(e) => setWorkflowForm({ ...workflowForm, name: e.target.value })}
                  placeholder="e.g., Express Delivery Workflow"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Workflow Type *
                </label>
                <select
                  value={workflowForm.workflow_type}
                  onChange={(e) => setWorkflowForm({ ...workflowForm, workflow_type: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="STANDARD">Standard Material</option>
                  <option value="JIT">Just-In-Time (JIT)</option>
                  <option value="FOC">Free of Charge (FOC)</option>
                  <option value="RETURN">Return Material</option>
                  <option value="CRITICAL">Critical Supplier</option>
                  <option value="CUSTOM">Custom</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={workflowForm.description}
                  onChange={(e) => setWorkflowForm({ ...workflowForm, description: e.target.value })}
                  placeholder="Describe when to use this workflow..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default SLA (hours) *
                </label>
                <input
                  type="number"
                  step="0.1"
                  required
                  value={workflowForm.default_sla_hours}
                  onChange={(e) => setWorkflowForm({ ...workflowForm, default_sla_hours: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={workflowForm.is_active}
                  onChange={(e) => setWorkflowForm({ ...workflowForm, is_active: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                  Active (available for use)
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowWorkflowModal(false);
                    resetWorkflowForm();
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {workflowForm.name ? 'Update' : 'Create'} Workflow
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create/Edit Step Modal */}
      {showStepModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingStep ? 'Edit' : 'Add'} Workflow Step
                </h2>
                <button
                  onClick={() => {
                    setShowStepModal(false);
                    setEditingStep(null);
                    resetStepForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={editingStep ? (e) => {
              e.preventDefault();
              updateStepMutation.mutate({ id: editingStep.id, data: stepForm });
            } : handleCreateStep} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Step Name *
                </label>
                <input
                  type="text"
                  required
                  value={stepForm.name}
                  onChange={(e) => setStepForm({ ...stepForm, name: e.target.value })}
                  placeholder="e.g., Quality Inspection"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Step Type *
                </label>
                <select
                  value={stepForm.step_type}
                  onChange={(e) => setStepForm({ ...stepForm, step_type: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {STEP_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label} - {type.description}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={stepForm.description}
                  onChange={(e) => setStepForm({ ...stepForm, description: e.target.value })}
                  placeholder="What happens in this step..."
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SLA (hours) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={stepForm.sla_hours}
                    onChange={(e) => setStepForm({ ...stepForm, sla_hours: parseFloat(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Assigned Role
                  </label>
                  <select
                    name="assigned_role"
                    value={stepForm.assigned_role}
                    onChange={(e) => setStepForm({ ...stepForm, assigned_role: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a role...</option>
                    <option value="Gate Security">Gate Security</option>
                    <option value="Warehouse Supervisor">Warehouse Supervisor</option>
                    <option value="Warehouse Operator">Warehouse Operator</option>
                    <option value="QC Inspector">QC Inspector</option>
                    <option value="Senior QC Inspector">Senior QC Inspector</option>
                    <option value="Logistics Coordinator">Logistics Coordinator</option>
                    <option value="System">System</option>
                    {roles.map(role => {
                      const roleName = role.name || role.display_name || role.id;
                      // Only add if not already in the hardcoded list
                      const hardcodedRoles = ['Gate Security', 'Warehouse Supervisor', 'Warehouse Operator', 'QC Inspector', 'Senior QC Inspector', 'Logistics Coordinator', 'System'];
                      if (!hardcodedRoles.includes(roleName)) {
                        return <option key={role.id} value={roleName}>{roleName}</option>;
                      }
                      return null;
                    })}
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowStepModal(false);
                    setEditingStep(null);
                    resetStepForm();
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingStep ? 'Update' : 'Add'} Step
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowConfiguration;
